/**
 * PdfPresentationViewer — viewer PDF tích hợp page sync + annotation overlay
 * cho phiên Virtual Courtroom (M06-3).
 *
 * Đặc điểm:
 *  - react-pdf render PDF từ blob URL (lấy qua loadAssetPreview của parent).
 *  - Page navigation: presenter chuyển trang → BE → SSE → mọi participant follow.
 *  - Annotation: rectangle overlay tọa độ fraction 0..1 (so với page render size).
 *    Presenter drag chuột để vẽ; viewer chỉ xem.
 *  - Realtime: parent inject `courtroom` (state mới nhất từ SSE polling).
 */
import { useEffect, useMemo, useRef, useState } from 'react'
import { Document, Page, pdfjs } from 'react-pdf'
import 'react-pdf/dist/Page/AnnotationLayer.css'
import 'react-pdf/dist/Page/TextLayer.css'
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import {
  ChevronLeft,
  ChevronRight,
  ClearAll,
  Brush,
  PanTool,
  EmojiPeople,
  Lock,
  LockOpen,
} from '@mui/icons-material'

import type { Courtroom } from '../store/api/courtroomsApi'
import {
  useAddAnnotationMutation,
  useClaimPresenterMutation,
  useClearAnnotationsMutation,
  useReleasePresenterMutation,
  useSetPresentationPageMutation,
} from '../store/api/presentationApi'

// Worker script: react-pdf 10 đi với pdfjs-dist 5; dùng URL local thay vì CDN
// để tránh CORS + offline. Vite tự bundle pdfjs worker khi import.
pdfjs.GlobalWorkerOptions.workerSrc = new URL(
  'pdfjs-dist/build/pdf.worker.min.mjs',
  import.meta.url,
).toString()


export interface PdfPresentationViewerProps {
  courtroom: Courtroom
  pdfBlobUrl: string  // URL.createObjectURL(pdfBlob) — caller giữ + revoke
  currentUserId?: string
  isHost: boolean      // admin / host_judge → có thể preempt presenter
}

export function PdfPresentationViewer({
  courtroom,
  pdfBlobUrl,
  currentUserId,
  isHost,
}: PdfPresentationViewerProps) {
  const [numPages, setNumPages] = useState<number>(0)
  const [renderSize, setRenderSize] = useState<{ w: number; h: number }>({ w: 0, h: 0 })
  const pageWrapRef = useRef<HTMLDivElement | null>(null)
  const [drawMode, setDrawMode] = useState(false)
  const [drag, setDrag] = useState<{ x0: number; y0: number; x: number; y: number } | null>(null)
  const [jumpInput, setJumpInput] = useState('')

  const [claimPresenter] = useClaimPresenterMutation()
  const [releasePresenter] = useReleasePresenterMutation()
  const [setPage] = useSetPresentationPageMutation()
  const [addAnnotation] = useAddAnnotationMutation()
  const [clearAnnotations] = useClearAnnotationsMutation()

  const currentPage = courtroom.presentation_current_page || 1
  const presenterId = courtroom.presenter_user_id || courtroom.shared_by_user_id
  const isPresenter = !!currentUserId && presenterId === currentUserId
  const canControl = isHost || isPresenter

  const annotations = useMemo(
    () => (courtroom.presentation_annotations || []).filter((a) => a.page === currentPage),
    [courtroom.presentation_annotations, currentPage],
  )

  // Resize observer cho page render — annotation overlay khớp pixel.
  useEffect(() => {
    const el = pageWrapRef.current
    if (!el) return
    const ro = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setRenderSize({
          w: entry.contentRect.width,
          h: entry.contentRect.height,
        })
      }
    })
    ro.observe(el)
    return () => ro.disconnect()
  }, [])

  const handlePrev = async () => {
    if (currentPage <= 1) return
    try {
      await setPage({ courtroomId: courtroom.id, page: currentPage - 1 }).unwrap()
    } catch (e: any) {
      alert(`Chuyển trang thất bại: ${e?.data?.detail || e?.message}`)
    }
  }
  const handleNext = async () => {
    if (numPages && currentPage >= numPages) return
    try {
      await setPage({ courtroomId: courtroom.id, page: currentPage + 1 }).unwrap()
    } catch (e: any) {
      alert(`Chuyển trang thất bại: ${e?.data?.detail || e?.message}`)
    }
  }
  const handleJump = async () => {
    const p = parseInt(jumpInput, 10)
    if (!Number.isFinite(p) || p < 1 || (numPages && p > numPages)) return
    try {
      await setPage({ courtroomId: courtroom.id, page: p }).unwrap()
      setJumpInput('')
    } catch (e: any) {
      alert(`Jump page thất bại: ${e?.data?.detail || e?.message}`)
    }
  }

  // Annotation drawing (presenter only)
  const beginDraw = (e: React.MouseEvent) => {
    if (!drawMode || !canControl) return
    const rect = pageWrapRef.current?.getBoundingClientRect()
    if (!rect) return
    const x = (e.clientX - rect.left) / rect.width
    const y = (e.clientY - rect.top) / rect.height
    setDrag({ x0: x, y0: y, x, y })
  }
  const moveDraw = (e: React.MouseEvent) => {
    if (!drag) return
    const rect = pageWrapRef.current?.getBoundingClientRect()
    if (!rect) return
    setDrag({
      ...drag,
      x: (e.clientX - rect.left) / rect.width,
      y: (e.clientY - rect.top) / rect.height,
    })
  }
  const endDraw = async () => {
    if (!drag) return
    const { x0, y0, x, y } = drag
    setDrag(null)
    const x1 = Math.min(x0, x)
    const y1 = Math.min(y0, y)
    const w = Math.abs(x - x0)
    const h = Math.abs(y - y0)
    // Bỏ qua click-no-drag
    if (w < 0.005 || h < 0.005) return
    try {
      await addAnnotation({
        courtroomId: courtroom.id,
        page: currentPage,
        x: x1,
        y: y1,
        w,
        h,
        color: '#ffeb3b',
      }).unwrap()
    } catch (e: any) {
      alert(`Vẽ annotation thất bại: ${e?.data?.detail || e?.message}`)
    }
  }

  const handleClearPage = async () => {
    if (!window.confirm(`Xoá toàn bộ annotation trên trang ${currentPage}?`)) return
    try {
      await clearAnnotations({ courtroomId: courtroom.id, page: currentPage }).unwrap()
    } catch (e: any) {
      alert(e?.data?.detail || e?.message)
    }
  }

  const handleClaim = async () => {
    try {
      await claimPresenter(courtroom.id).unwrap()
    } catch (e: any) {
      alert(e?.data?.detail || e?.message)
    }
  }
  const handleRelease = async () => {
    try {
      await releasePresenter(courtroom.id).unwrap()
    } catch (e: any) {
      alert(e?.data?.detail || e?.message)
    }
  }

  return (
    <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
      {/* Toolbar */}
      <Stack
        direction="row"
        spacing={1}
        sx={{
          p: 1,
          alignItems: 'center',
          flexWrap: 'wrap',
          borderBottom: '1px solid',
          borderColor: 'divider',
          bgcolor: 'background.paper',
        }}
      >
        <Tooltip title="Trang trước">
          <span>
            <IconButton size="small" onClick={handlePrev} disabled={!canControl || currentPage <= 1}>
              <ChevronLeft />
            </IconButton>
          </span>
        </Tooltip>
        <Typography variant="body2" fontWeight={600}>
          {currentPage} / {numPages || '?'}
        </Typography>
        <Tooltip title="Trang sau">
          <span>
            <IconButton
              size="small"
              onClick={handleNext}
              disabled={!canControl || (!!numPages && currentPage >= numPages)}
            >
              <ChevronRight />
            </IconButton>
          </span>
        </Tooltip>
        <TextField
          size="small"
          type="number"
          placeholder="Trang"
          value={jumpInput}
          onChange={(e) => setJumpInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') handleJump()
          }}
          inputProps={{ min: 1, max: numPages || undefined, style: { width: 60 } }}
          disabled={!canControl}
        />
        <Box sx={{ flex: 1 }} />
        {canControl && (
          <>
            <Tooltip title={drawMode ? 'Tắt chế độ vẽ' : 'Vẽ rectangle highlight'}>
              <IconButton
                size="small"
                onClick={() => setDrawMode((v) => !v)}
                color={drawMode ? 'primary' : 'default'}
              >
                {drawMode ? <Brush /> : <PanTool />}
              </IconButton>
            </Tooltip>
            <Tooltip title="Xoá annotation trang hiện tại">
              <IconButton size="small" onClick={handleClearPage} disabled={annotations.length === 0}>
                <ClearAll />
              </IconButton>
            </Tooltip>
          </>
        )}
        {presenterId && (
          <Chip
            size="small"
            icon={<EmojiPeople fontSize="small" />}
            label={isPresenter ? 'Bạn đang trình bày' : 'Đang trình bày'}
            color={isPresenter ? 'success' : 'info'}
            variant="outlined"
          />
        )}
        {!isPresenter && (
          <Tooltip
            title={
              presenterId && !isHost
                ? 'Đã có người trình bày. Liên hệ Chủ tọa để chuyển.'
                : 'Lấy quyền điều khiển'
            }
          >
            <span>
              <Button
                size="small"
                variant="outlined"
                startIcon={<LockOpen />}
                onClick={handleClaim}
                disabled={!!presenterId && !isHost}
              >
                {presenterId ? 'Chuyển quyền' : 'Trình bày'}
              </Button>
            </span>
          </Tooltip>
        )}
        {isPresenter && (
          <Button
            size="small"
            variant="outlined"
            color="warning"
            startIcon={<Lock />}
            onClick={handleRelease}
          >
            Giải phóng
          </Button>
        )}
      </Stack>

      {/* PDF stage */}
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          overflow: 'auto',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'flex-start',
          bgcolor: '#444',
          p: 2,
        }}
        onMouseDown={beginDraw}
        onMouseMove={moveDraw}
        onMouseUp={endDraw}
        onMouseLeave={() => setDrag(null)}
      >
        <Box
          ref={pageWrapRef}
          sx={{
            position: 'relative',
            cursor: drawMode && canControl ? 'crosshair' : 'default',
            userSelect: drawMode ? 'none' : 'auto',
            boxShadow: '0 8px 24px rgba(0,0,0,.4)',
          }}
        >
          <Document
            file={pdfBlobUrl}
            onLoadSuccess={(d) => setNumPages(d.numPages)}
            loading={<CircularProgress size={20} sx={{ color: '#fff' }} />}
            error={<Box sx={{ p: 2, color: '#fff' }}>Lỗi load PDF.</Box>}
          >
            <Page
              pageNumber={currentPage}
              renderAnnotationLayer={false}
              renderTextLayer={false}
              width={Math.min(960, window.innerWidth - 80)}
            />
          </Document>

          {/* Annotation overlay — render TRÊN page */}
          {renderSize.w > 0 &&
            annotations.map((a) => (
              <Box
                key={a.id}
                sx={{
                  position: 'absolute',
                  left: `${a.x * 100}%`,
                  top: `${a.y * 100}%`,
                  width: `${a.w * 100}%`,
                  height: `${a.h * 100}%`,
                  bgcolor: `${a.color || '#ffeb3b'}33`,
                  border: `2px solid ${a.color || '#ffeb3b'}`,
                  pointerEvents: 'none',
                  borderRadius: 0.5,
                }}
              />
            ))}

          {/* Drag preview rectangle */}
          {drag && (
            <Box
              sx={{
                position: 'absolute',
                left: `${Math.min(drag.x0, drag.x) * 100}%`,
                top: `${Math.min(drag.y0, drag.y) * 100}%`,
                width: `${Math.abs(drag.x - drag.x0) * 100}%`,
                height: `${Math.abs(drag.y - drag.y0) * 100}%`,
                bgcolor: 'rgba(255,235,59,0.18)',
                border: '2px dashed #ffeb3b',
                pointerEvents: 'none',
                borderRadius: 0.5,
              }}
            />
          )}
        </Box>
      </Box>
    </Box>
  )
}
